import { computed, Ref } from 'vue';
import type { Content } from '@ilteducation/content-registry-types';
import useCollectionContentsStore from './collection-contents';
import { ContentQuery, ContentFilters } from './types';
import { isPredefinedCollection } from './utils';

const useCollectionContents = (
  collectionId: Ref<string>,
  query: Ref<ContentQuery>,
  filters?: Ref<ContentFilters>,
  autoLoad = true,
) => {
  const { contents, loadCollectionContents, next, isLoading, hasMore } = useCollectionContentsStore();
  const filteredContent = computed(() => {
    const content = contents(collectionId);
    // TODO need to make it universal and add filter type. Hardcoded for now
    return content.filter((item: Content) => {
      if (!filters) {
        return true;
      }
      // Type first: if the surface doesn't want this kind of content at all, nothing else matters.
      if (filters.value.contentTypes?.length && !filters.value.contentTypes.includes(item.type)) {
        return false;
      }
      const filtersChecks = [];
      if ('ages' in item && filters.value.ages.length > 0) {
        if (!item?.ages?.max && !item?.ages?.min) {
          return false;
        }
        // If book doesn't contain min or max value we are considering that there is just no limit
        const upperAge = item?.ages?.max ?? filters.value.ages[1];
        const formattedUpperAge = typeof upperAge === 'string' ? parseInt(upperAge, 10) : upperAge;
        const lowerAge = item?.ages?.min ?? 0;
        const formattedLowerAge = typeof lowerAge === 'string' ? parseInt(lowerAge, 10) : lowerAge;
        filtersChecks.push(
          () => filters.value.ages[0] <= formattedUpperAge && filters.value.ages[1] >= formattedLowerAge,
        );
      }
      if ('accessRules' in item) {
        filtersChecks.push(() => item.accessRules.subscriptionType.includes(filters.value.subscription));
      }
      if (filters.value.audioLanguages.length > 0 && 'languages' in item && item.languages?.audio) {
        filtersChecks.push(() => item!.languages!.audio!.some((r) => filters.value.audioLanguages.indexOf(r) >= 0));
      }
      if ('languages' in item && item.languages.text !== undefined && filters.value.textLanguages.length > 0) {
        // text language only one
        filtersChecks.push(() => item!.languages!.text!.includes(filters.value.textLanguages));
      }

      return filtersChecks.every((cb) => cb());
    });
  });

  const loadMore = () => {
    if (isLoading(collectionId) || !hasMore(collectionId)) {
      return;
    }

    next(collectionId.value);
  };

  // trigger initial load and resets
  if (autoLoad && contents(collectionId).length === 0) {
    loadCollectionContents(collectionId.value, query.value);
  }

  const isPredefined = computed(() => isPredefinedCollection(collectionId));

  return {
    contents: filteredContent,
    isLoading: computed(() => isLoading(collectionId)),
    hasMore: computed(() => hasMore(collectionId)),
    next: loadMore,
    isPredefined,
  };
};

export default useCollectionContents;
